import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { Brand } from "../components/Shell";
import { Button } from "../components/ui/button";

const Frame = ({ children }) => (
  <div className="min-h-screen bg-slate-50">
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto flex h-16 max-w-3xl items-center px-5">
        <Brand />
      </div>
    </header>
    <main className="mx-auto max-w-lg px-5 py-20">
      <div className="ct-rise rounded-lg border border-slate-200 bg-white p-10 text-center shadow-sm">
        {children}
      </div>
    </main>
  </div>
);

const Back = () => (
  <Link to="/">
    <Button
      data-testid="payment-back-button"
      className="mt-8 h-11 rounded-full bg-blue-600 px-7 font-semibold hover:bg-blue-700"
    >
      Back to dashboard
    </Button>
  </Link>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) return setState("error");
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState("paid");
        if (["failed", "expired"].includes(data.payment_status)) return setState("error");
      } catch (e) {
        if (tries > 3) return setState("error");
      }
      if (tries >= 10) return setState("timeout");
      setTimeout(tick, 2000);
    };
    tick();
  }, [sessionId]);

  return (
    <Frame>
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-600" />
          <h1 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-slate-900">
            Confirming your payment…
          </h1>
          <p className="mt-2 text-sm text-slate-500">This usually takes a few seconds.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
          <h1 className="mt-6 font-display text-3xl font-black tracking-tight text-slate-900">
            Subscription active
          </h1>
          <p className="mt-3 text-base text-slate-600">
            The Vendor/Supplier is now marked as paid and their admin can start onboarding
            customers.
          </p>
          <Back />
        </div>
      )}
      {(state === "error" || state === "timeout") && (
        <div data-testid="payment-pending">
          <XCircle className="mx-auto h-12 w-12 text-amber-500" />
          <h1 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-slate-900">
            {state === "timeout" ? "Still processing" : "We couldn't confirm this payment"}
          </h1>
          <p className="mt-3 text-base text-slate-600">
            The Vendor/Supplier stays marked as pending. You can retry the plan from the dashboard.
          </p>
          <Back />
        </div>
      )}
    </Frame>
  );
}

export function PaymentCancel() {
  return (
    <Frame>
      <div data-testid="payment-cancelled">
        <XCircle className="mx-auto h-12 w-12 text-slate-400" />
        <h1 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-slate-900">
          Checkout cancelled
        </h1>
        <p className="mt-3 text-base text-slate-600">
          No payment was taken. The Vendor/Supplier remains pending until a plan is paid.
        </p>
        <Back />
      </div>
    </Frame>
  );
}
